/**
 * Admin React Query hooks
 *
 * Query and mutation hooks for admin endpoints.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  adminApi,
  type AdminUserRolesUpdate,
  type AdminUserUpdate,
  type AIUsageDailyWindow,
} from "@/services/admin";

// ── Query key factory ──────────────────────────────────────

export const adminKeys = {
  all: ["admin"] as const,
  dashboard: () => [...adminKeys.all, "dashboard"] as const,
  metrics: (period: string, metric: string) =>
    [...adminKeys.all, "metrics", period, metric] as const,
  users: () => [...adminKeys.all, "users"] as const,
  userList: (params?: {
    search?: string;
    plan?: string;
    status?: string;
    createdFrom?: string;
    createdTo?: string;
    cursor?: string;
  }) => [...adminKeys.users(), "list", params] as const,
  userDetail: (id: string) => [...adminKeys.users(), "detail", id] as const,
  content: () => [...adminKeys.all, "content"] as const,
  contentList: (params?: {
    type?: string;
    q?: string;
    createdFrom?: string;
    createdTo?: string;
    cursor?: string;
  }) => [...adminKeys.content(), "list", params] as const,
  contentDetail: (id: string, params?: { userId?: string; type?: string }) =>
    [...adminKeys.content(), "detail", id, params] as const,
  aiUsage: () => [...adminKeys.all, "ai-usage"] as const,
  aiUsageDaily: (days: number) =>
    [...adminKeys.all, "ai-usage-daily", days] as const,
  audit: () => [...adminKeys.all, "audit"] as const,
  auditList: (params?: {
    date?: string;
    action?: string;
    actorId?: string;
    actorEmail?: string;
    cursor?: string;
  }) => [...adminKeys.audit(), "list", params] as const,
  feedback: () => [...adminKeys.all, "feedback"] as const,
  feedbackList: (params?: {
    type?: string;
    status?: string;
    cursor?: string;
  }) => [...adminKeys.feedback(), "list", params] as const,
  feedbackDetail: (id: string) =>
    [...adminKeys.feedback(), "detail", id] as const,
  logs: () => [...adminKeys.all, "logs"] as const,
  logGroups: () => [...adminKeys.logs(), "groups"] as const,
  logEvents: (params?: {
    logGroup: string;
    startTime?: number;
    endTime?: number;
    filter?: string;
  }) => [...adminKeys.logs(), "events", params] as const,
};

// ── Queries ────────────────────────────────────────────────

/**
 * Admin queries use a 5-minute staleTime to match the backend's
 * in-memory cache TTL. This prevents redundant Lambda invocations
 * when navigating between admin pages.
 */
const ADMIN_STALE_TIME = 5 * 60_000;

/**
 * How often the dashboard refetches on its own, so a page left open keeps
 * showing current numbers without a manual refresh. Matches the backend's
 * own 5-minute cache TTL — polling faster would just re-read the same cache.
 */
const DASHBOARD_REFETCH_INTERVAL = 5 * 60_000;

export function useAdminDashboard() {
  return useQuery({
    queryKey: adminKeys.dashboard(),
    queryFn: () => adminApi.getDashboard(),
    staleTime: ADMIN_STALE_TIME,
    refetchInterval: DASHBOARD_REFETCH_INTERVAL,
    // Keep refreshing even while the tab is in the background — an admin
    // watching a second monitor should not come back to stale figures.
    refetchIntervalInBackground: true,
  });
}

export function useAdminMetrics(period: string, metric: string) {
  return useQuery({
    queryKey: adminKeys.metrics(period, metric),
    queryFn: () => adminApi.getMetrics({ period, metric }),
    staleTime: ADMIN_STALE_TIME,
  });
}

export function useAdminUsers(params?: {
  search?: string;
  plan?: string;
  status?: "active" | "disabled";
  createdFrom?: string;
  createdTo?: string;
  cursor?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: adminKeys.userList(params),
    queryFn: () => adminApi.listUsers(params),
    staleTime: ADMIN_STALE_TIME,
  });
}

export function useAdminUser(userId: string) {
  return useQuery({
    queryKey: adminKeys.userDetail(userId),
    queryFn: () => adminApi.getUser(userId),
    enabled: !!userId,
    staleTime: ADMIN_STALE_TIME,
  });
}

export function useAdminContent(params?: {
  type?: string;
  q?: string;
  createdFrom?: string;
  createdTo?: string;
  cursor?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: adminKeys.contentList(params),
    queryFn: () => adminApi.listContent(params),
    staleTime: ADMIN_STALE_TIME,
  });
}

/** One content item's full detail — the id alone cannot locate it; see adminApi.getContent. */
export function useAdminContentDetail(
  contentId: string | undefined,
  params: { userId: string; type?: "source" | "draft" } | undefined
) {
  return useQuery({
    queryKey: adminKeys.contentDetail(contentId ?? "", params),
    queryFn: () => adminApi.getContent(contentId!, params!),
    enabled: Boolean(contentId && params?.userId),
    staleTime: ADMIN_STALE_TIME,
  });
}

export function useAdminAIUsage() {
  return useQuery({
    queryKey: adminKeys.aiUsage(),
    queryFn: () => adminApi.getAIUsage(),
    staleTime: ADMIN_STALE_TIME,
  });
}

export function useAdminAIUsageDaily(days: AIUsageDailyWindow) {
  return useQuery({
    queryKey: adminKeys.aiUsageDaily(days),
    queryFn: () => adminApi.getAIUsageDaily(days),
    staleTime: ADMIN_STALE_TIME,
  });
}

export function useAdminAuditLogs(params?: {
  date?: string;
  cursor?: string;
  limit?: number;
  action?: string;
  actorId?: string;
  actorEmail?: string;
}) {
  return useQuery({
    queryKey: adminKeys.auditList(params),
    queryFn: () => adminApi.getAuditLogs(params),
    staleTime: ADMIN_STALE_TIME,
  });
}

export function useAdminFeedback(params?: {
  type?: string;
  status?: string;
  cursor?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: adminKeys.feedbackList(params),
    queryFn: () => adminApi.listFeedback(params),
    staleTime: ADMIN_STALE_TIME,
  });
}

export function useAdminFeedbackDetail(feedbackId: string) {
  return useQuery({
    queryKey: adminKeys.feedbackDetail(feedbackId),
    queryFn: () => adminApi.getFeedback(feedbackId),
    enabled: !!feedbackId,
    staleTime: ADMIN_STALE_TIME,
  });
}

// ── CloudWatch Logs queries ────────────────────────────────

export function useAdminLogGroups() {
  return useQuery({
    queryKey: adminKeys.logGroups(),
    queryFn: () => adminApi.getLogGroups(),
    staleTime: ADMIN_STALE_TIME,
  });
}

export function useAdminLogEvents(
  params: {
    logGroup: string;
    startTime?: number;
    endTime?: number;
    filter?: string;
    limit?: number;
  },
  options?: { refetchInterval?: number | false }
) {
  return useQuery({
    queryKey: adminKeys.logEvents(params),
    queryFn: () => adminApi.getLogEvents(params),
    enabled: !!params.logGroup,
    staleTime: 0, // Always refetch for live tail
    refetchInterval: options?.refetchInterval ?? false,
  });
}

// ── Mutations ──────────────────────────────────────────────

export function useUpdateAdminUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: AdminUserUpdate }) =>
      adminApi.updateUser(userId, data),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: adminKeys.userDetail(userId) });
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

export function useDisableAdminUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, disabled }: { userId: string; disabled: boolean }) =>
      adminApi.disableUser(userId, disabled),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: adminKeys.userDetail(userId) });
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

export function useResetAdminPassword() {
  return useMutation({
    mutationFn: (userId: string) => adminApi.resetPassword(userId),
  });
}

export function useUpdateAdminUserRoles() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: string;
      data: AdminUserRolesUpdate;
    }) => adminApi.updateUserRoles(userId, data),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: adminKeys.userDetail(userId) });
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

export function useRecalculateStreaks() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => adminApi.recalculateStreaks(userId),
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: adminKeys.userDetail(userId) });
    },
  });
}

export function useUpdateAdminFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      feedbackId,
      data,
    }: {
      feedbackId: string;
      data: { status?: string; priority?: string; adminNotes?: string };
    }) => adminApi.updateFeedback(feedbackId, data),
    onSuccess: (_, { feedbackId }) => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.feedbackDetail(feedbackId),
      });
      queryClient.invalidateQueries({ queryKey: adminKeys.feedback() });
    },
  });
}

export function useDeleteAdminFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (feedbackId: string) => adminApi.deleteFeedback(feedbackId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.feedback() });
    },
  });
}
